import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Popup from "../common/Popup";
import {
    DKLabel,
    DKButton,
    DKInput,
    INPUT_TYPE,
    INPUT_VIEW_DIRECTION,
    showLoader,
    removeLoader,
    showToast,
    TOAST_TYPE
} from "deskera-ui-library";
import { fetchEmployeeListWithoutFilter, selectEmployeeListWithoutLimit } from "../../Redux/Slices/EmployeeListSlice";
import EmployeeListService from "../../services/EmployeeListService";
import ShiftManagementService from "../../services/ShiftManagementService";
import ShiftUtils from "./ShiftUtils";
import { fetchShifts, selectShifts } from "../../Redux/Slices/ShiftManagementSlice";

function AddMember(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const initialFormState = {
        members: {
            id: "members",
            title: t("MEMBERS"),
            type: INPUT_TYPE.MULTI_SELECT
        }
    }

    const shifts = useSelector(selectShifts);
    const employeeResponse = useSelector(selectEmployeeListWithoutLimit);

    const [formState, setFormState] = useState(initialFormState);
    const [shift, setShift] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState(null);

    useEffect( async () => {
        if (ShiftManagementService.SHIFT) {
            setShift(ShiftManagementService.SHIFT);
            ShiftManagementService.SHIFT = null;
        } else {
            props.onClose();
        }

        fetchDependencies();
    }, []);

    useEffect(() => {
        if (employeeOptions !== null) {
            const newFormState = {...formState};
            newFormState.members.options = Array.isArray(employeeOptions) ? employeeOptions : [];
            setFormState(newFormState);
        }
    }, [employeeOptions]);

    useEffect(() => {
        if (shifts && employeeResponse?.users?.length > 0) {
            const users = ShiftUtils.getMembersWithoutShift(shifts, employeeResponse.users);
            setEmployeeOptions(users);
        }
    }, [employeeResponse, shifts]);

    const fetchDependencies = async () => {
        EmployeeListService.apiConfig = {
            customJson: {
                "orderBy.field": "updatedAt",
                "orderBy.order": "desc"
            }
        };

        showLoader();
        await Promise.all([dispatch(fetchEmployeeListWithoutFilter()), dispatch(fetchShifts())]);
        removeLoader();
    }

    const getMultiSelectDropdownValue = (v, o) => {
        const value = [];

        if (Array.isArray(v)) {
            v.forEach((i) => {
                const option = o.find((item, index) => index === i);
                if (option) {
                    value.push(option.value);
                }
            });
        }

        return value;
    }

    const isRequired = (validation) => {
        return validation ? validation.findIndex((v) => v[0] === "required") !== -1 : false;
    }

    const showError = (errorMessage) => {
        if (errorMessage) {
            return (
                <div style={{
                    marginTop: "-19px",
                    marginBottom: "8px"
                }}>
                    <DKLabel
                        className="ml-xs fs-s text-red"
                        text={errorMessage}
                    />
                </div>
            )
        }
    }

    const hasError = (field, value) => {
        let error = {
            invalid: false,
            errorMessage: null
        };

        const validation = field.validation;

        if (validation && validation.length !== 0) {
            validation.forEach((validation) => {
                switch (validation[0]) {
                    case "required":
                        if (value === null || value === undefined || String(value).trim() === "") {
                            error.invalid = true;
                        }
                        break;
                    default:
                        error.invalid = false;
                        error.errorMessage = null;
                        break;
                }
            });
        }

        return error;
    }

    const updateState = (stateName, field, value) => {
        const state = {...formState};

        state[stateName] = {...field, value: value, ...hasError(field, value)};

        setFormState(state);
    }

    const isInvalid = () => {
        let invalid = false;

        const state = {...formState};
        Object.entries(state).forEach(([key, field]) => {
            const error = hasError(field, field.value);
            field.invalid = error.invalid;
            field.errorMessage = error.errorMessage;

            if (field.invalid) {
                invalid = true;
            }
        });

        setFormState(state);

        return invalid;
    }

    const getPayload = () => {
        const payload = {...shift};

        const selectedMembers = getMultiSelectDropdownValue(formState.members.value, formState.members.options);
        if (selectedMembers?.length > 0) {
            payload.employees = payload.employees.map((e) => e.employeeId);
            const employees = Array.isArray(payload.employees) ? [...payload.employees] : [];
            selectedMembers.forEach((member) => {
                if (!employees.includes(member)) {
                    employees.push(member);
                }
            });

            payload.employees = employees;
        }

        delete payload.employeeDetails;

        return payload;
    }

    const saveMember = () => {
        if (isInvalid()) {
            return;
        }

        const payload = getPayload();
        showLoader(t("ADDDING_MEMBER"));
        ShiftManagementService.updateShift(payload).then(
            (response) => {
                removeLoader();
                if (response) {
                    showToast(t("ADD_MEMBER_SUCCESS"), TOAST_TYPE.SUCCESS);
                    props.onClose(true);
                } else {
                    showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                }
            }, (error) => {
                removeLoader();
                if (error?.errorMessage) {
                    showToast(error.errorMessage, TOAST_TYPE.FAILURE);
                } else {
                    showToast(t("INTERNAL_SERVER_ERROR"), TOAST_TYPE.FAILURE);
                }
            }
        );
    }

    const renderField = (stateName, field, width) => {
        switch (field.type) {
            case INPUT_TYPE.MULTI_SELECT:
                return (
                    <div key={field.id} style={{ flex: "0 0 100%", maxWidth: width }}>
                        <div className="row pb-l mb-s position-relative">
                            <DKInput
                                title={field.title}
                                type={INPUT_TYPE.DROPDOWN}
                                direction={INPUT_VIEW_DIRECTION.HORIZONTAL}
                                dropdownConfig={{
                                    multiSelect: true,
                                    checkMarkColor: "bg-app",
                                    allowSearch: field.options?.length > 6,
                                    searchableKey: "label",
                                    data: field.options,
                                    renderer: (i, item) => item.label,
                                    onSelect: (i, value) => {},
                                }}
                                displayKey={"label"}
                                value={field.value}
                                errorMessage={field.errorMessage}
                                readOnly={!!field.readOnly}
                                required={isRequired(field.validation)}
                                invalid={field.invalid}
                                canValidate={field.invalid}
                                onChange={(value) => {
                                    updateState(stateName, field, value);
                                }}
                            />
                        </div>
                        {field.invalid && showError(field.errorMessage)}
                    </div>
                );
            default:
                break;
        }
    }

    return (
        <Popup popupClass={"popup-overflow"}>
            <div className="parent-width">
                <div className="row">
                    <DKLabel text={t("ADD_MEMBER")} className="fs-l fw-b" />
                    <div className="ml-auto d-flex">
                        <DKButton
                            title={t("CANCEL")}
                            className="border-radius-m bg-gray1 border-m"
                            onClick={() => {
                                props.onClose();
                            }}
                        />
                        <DKButton
                            title={t("SAVE")}
                            className="bg-app text-white ml-m"
                            onClick={() => {
                                saveMember();
                            }}
                        />
                    </div>
                </div>
                <div className="mt-l">
                    {renderField("members", formState.members, "100%")}
                </div>
            </div>
        </Popup>
    );
}

export default AddMember;
